import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { Product } from '../../types';
import { formatWindowClosedMonth } from '../../utils/productWindows';
import { ArrowLeft, Loader2 } from 'lucide-react';

// A vault piece is presented like a work in a collection: one image at a
// time, a placard of recorded facts, a curatorial note. No price, no cart —
// the vault holds, it does not sell.
const VaultItemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState('');

  useEffect(() => {
    const fetchItem = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'products', id));
        if (snap.exists()) {
          const item = { id: snap.id, ...snap.data() } as Product;
          setProduct(item);
          setActiveImage(item.image);
        } else {
          navigate('/vault', { replace: true });
        }
      } catch (error) {
        console.error('Error fetching vault item:', error);
        navigate('/vault', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex justify-center items-center">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  if (!product) return null;

  const images = [product.image, ...(product.gallery || [])].filter(
    (src, i, arr) => src && arr.indexOf(src) === i
  );

  const year = product.releaseDate
    ? new Date(product.releaseDate).getFullYear()
    : undefined;

  const editionLine = product.editionNumber
    ? product.totalEditions
      ? `${product.editionNumber} of ${product.totalEditions}`
      : `${product.editionNumber}`
    : product.totalEditions
      ? `Edition of ${product.totalEditions}`
      : undefined;

  const closedLine = product.windowCloseAt
    ? `Window closed ${formatWindowClosedMonth(product.windowCloseAt)}`
    : undefined;

  // The placard: only recorded facts appear — absent fields leave no gap
  const placard: { label: string; value?: string }[] = [
    { label: 'Artist', value: product.artistName },
    { label: 'Year', value: year?.toString() },
    { label: 'Medium', value: product.medium },
    { label: 'Dimensions', value: product.dimensions },
    { label: 'Edition', value: editionLine },
    { label: 'Fragment', value: product.fragmentNumber ? `No. ${product.fragmentNumber}` : undefined },
    { label: 'Status', value: closedLine || 'Held in the vault' },
  ];

  return (
    <div className="pt-24 min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 pb-24">
        <Link
          to="/vault"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-black transition-colors mb-8"
        >
          <ArrowLeft size={14} /> The Vault
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* The work */}
          <div className="lg:col-span-7">
            <div className="relative aspect-[3/4] bg-[#f4f4f4] overflow-hidden">
              <img
                src={activeImage || '/assets/placeholder.svg'}
                alt={product.title}
                onError={(e) => {
                  const t = e.currentTarget;
                  if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                }}
                className="absolute inset-0 w-full h-full object-contain"
              />
            </div>

            {images.length > 1 && (
              <div className="flex gap-2 mt-3">
                {images.map((src) => (
                  <button
                    key={src}
                    onClick={() => setActiveImage(src)}
                    className={`relative w-16 h-20 bg-[#f4f4f4] overflow-hidden border transition-colors ${
                      activeImage === src ? 'border-black' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* The placard */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-3">
                From the vault
              </p>
              <h1 className="font-display text-3xl md:text-4xl tracking-wide uppercase mb-1">
                {product.title}
              </h1>
              {(product.artistName || year) && (
                <p className="font-heading text-sm tracking-widest text-gray-500 uppercase mb-8">
                  {product.artistName}{product.artistName && year ? ', ' : ''}{year}
                </p>
              )}

              <dl className="border-t border-gray-200">
                {placard.filter(row => row.value).map(row => (
                  <div
                    key={row.label}
                    className="flex justify-between gap-6 py-3 border-b border-gray-100"
                  >
                    <dt className="font-mono text-[11px] uppercase tracking-widest text-gray-400">
                      {row.label}
                    </dt>
                    <dd className="font-body text-sm text-right">{row.value}</dd>
                  </div>
                ))}
              </dl>

              {product.description && (
                <div className="mt-10">
                  <h2 className="font-mono text-[11px] uppercase tracking-widest text-gray-400 mb-3">
                    Curatorial note
                  </h2>
                  <p className="font-body text-sm leading-relaxed text-gray-700 whitespace-pre-line">
                    {product.description}
                  </p>
                </div>
              )}

              {product.provenance && (
                <div className="mt-8">
                  <h2 className="font-mono text-[11px] uppercase tracking-widest text-gray-400 mb-3">
                    Provenance
                  </h2>
                  <p className="font-body text-sm leading-relaxed text-gray-700 whitespace-pre-line">
                    {product.provenance}
                  </p>
                </div>
              )}

              {product.artistBio && (
                <div className="mt-8">
                  <h2 className="font-mono text-[11px] uppercase tracking-widest text-gray-400 mb-3">
                    About the artist
                  </h2>
                  <p className="font-body text-sm leading-relaxed text-gray-700 whitespace-pre-line">
                    {product.artistBio}
                  </p>
                </div>
              )}

              <p className="mt-10 font-mono text-[11px] uppercase tracking-widest text-gray-400">
                Held, not sold.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaultItemDetail;
